import type { ReactNode } from 'react';
import { Text } from '@launchpad-ui/components';
import type { IconProps } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

type IconName = IconProps['name'];

import { headerContainer, headerIcon, headerLabel, headerTrailingIcons } from './styles.css';

export interface HeaderProps {
  /**
   * The label to display in the header.
   */
  label: ReactNode;

  /**
   * Optional leading icon name from LaunchPad icons.
   */
  leadingIcon?: IconName;

  /**
   * Optional trailing icon names from LaunchPad icons.
   */
  trailingIcons?: IconName[];

  /**
   * Optional className for custom styling.
   */
  className?: string;
}

/**
 * A simple header label component for table columns.
 * Renders a label with optional leading and trailing icons.
 *
 * @example
 * ```tsx
 * <Header label="Name" />
 * <Header label="Status" leadingIcon="warning-circle" />
 * <Header label={<CustomComponent />} trailingIcons={['sort-asc']} />
 * ```
 */
export const Header = ({ label, leadingIcon, trailingIcons, className }: HeaderProps) => {
  const labelContent =
    typeof label === 'string' ? (
      <Text size="medium" maxLines={1} style={{ whiteSpace: 'nowrap' }}>
        {label}
      </Text>
    ) : (
      label
    );

  return (
    <div className={clsx(headerContainer, className)}>
      {leadingIcon && (
        <div className={headerIcon}>
          <Icon name={leadingIcon} size="small" />
        </div>
      )}
      <div className={headerLabel}>{labelContent}</div>
      {trailingIcons && trailingIcons.length > 0 && (
        <div className={headerTrailingIcons}>
          {trailingIcons.map((iconName) => (
            <Icon key={iconName} name={iconName} size="small" variant="subtle" />
          ))}
        </div>
      )}
    </div>
  );
};
